import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchMe, getToken, login as apiLogin, logoutApi, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=checking, null=not authed, obj=authed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { setUser(null); setLoading(false); return; }
    fetchMe()
      .then((u) => setUser(u))
      .catch(() => { setToken(null); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await apiLogin(email, password);
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    await logoutApi();
    setToken(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  const refresh = useCallback(async () => {
    try { const u = await fetchMe(); setUser(u); return u; } catch { return null; }
  }, []);

  const hasRole = useCallback((min) => {
    if (!user) return false;
    const levels = { viewer: 0, editor: 1, admin: 2 };
    return (levels[user.role] ?? 0) >= (levels[min] ?? 0);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
